import { httpClient } from '@shared/api'
import type { PaginatedList } from '@shared/api/types'
import type { Message, MessageDTO, Post, PostDTO, PostType } from '../model/types'
import { mapCreatePostArgsToDTO, mapMessageFromDTO, mapPostFromDTO } from './mappers'
import type {
    CreatePostArgs,
    CreatePostTypeArg,
    EditPostArgs,
    GetPostArgs,
    PreviewPostArgs,
    ShareLinkArgs
} from './types'

export const getPosts = async ({
    page,
    pageSize,
    state,
    startDate,
    endDate,
    currentChannel,
    isAdvertising,
    adCampaignId,
    postTypes
}: GetPostArgs): Promise<PaginatedList<Post>> => {
    const {
        data: { results, next, previous, count }
    } = await httpClient.get<PaginatedList<PostDTO>>(`/posts`, {
        params: {
            page,
            page_size: pageSize,
            state,
            publish_date_start: startDate?.toISOString(),
            publish_date_end: endDate?.toISOString(),
            channel: currentChannel?.id,
            is_advertising: isAdvertising,
            ad_campaign: adCampaignId,
            post_types: postTypes
        }
    })
    return {
        count,
        next,
        previous,
        results: results.map(mapPostFromDTO)
    }
}

export const getPost = async (postId: number) => {
    const { data: result } = await httpClient.get<PostDTO>(`/posts/${postId}`)

    return mapPostFromDTO(result)
}

export const createPost = async (data: CreatePostArgs): Promise<Post> => {
    const { data: result } = await httpClient.post<PostDTO>('/posts', mapCreatePostArgsToDTO(data))
    return mapPostFromDTO(result)
}

export const publishPostNow = async (id: number): Promise<string> => {
    const { data: result } = await httpClient.post<{ detail: string }>(`/posts/${id}/now`)
    return result.detail
}

export const previewPost = async (data: PreviewPostArgs): Promise<string> => {
    const { data: result } = await httpClient.post<{ detail: string }>(`/posts/preview`, mapCreatePostArgsToDTO(data))
    return result.detail
}

export const getPostsTypes = async (channelId?: number): Promise<PostType[]> => {
    const { data: result } = await httpClient.get<PostType[]>(`/posts/types`, {
        params: {
            channel: channelId
        }
    })
    return result
}
export const deletePostTypes = async (id: number): Promise<void> => {
    const { data: result } = await httpClient.delete<void>(`/posts/types/${id}`)
    return result
}

export const createPostType = async (data: CreatePostTypeArg): Promise<void> => {
    const { data: result } = await httpClient.post<void>('/posts/types', data)
    return result
}

export const editPost = async (data: EditPostArgs): Promise<Post> => {
    const { data: result } = await httpClient.put<PostDTO>(`/posts/${data.id}`, mapCreatePostArgsToDTO(data))
    return mapPostFromDTO(result)
}

export const deletePost = async (id: number): Promise<void> => {
    await httpClient.delete<void>(`/posts/${id}`)
}

export const deleteFromTgPost = async (id: number): Promise<void> => {
    await httpClient.post<void>(`/posts/${id}/delete-from-tg`)
}

export const createShareLink = async (id?: number): Promise<{ id?: number; sharedId: string }> => {
    const {
        data: { share_id }
    } = await httpClient.post<{ share_id: string }>(`/posts/${id}/share`)
    return {
        id: id || undefined,
        sharedId: share_id
    }
}

export const getShareLink = async ({ id, sharedId }: ShareLinkArgs): Promise<void> => {
    await httpClient.get<void>(`/posts/${id}/share/${sharedId}`)
}

export const deleteShareLink = async ({ id, sharedId }: ShareLinkArgs): Promise<void> => {
    await httpClient.delete<void>(`/posts/${id}/share/${sharedId}`)
}

export const preparePostForTgShare = async (postId: number): Promise<number> => {
    const {
        data: { id }
    } = await httpClient.post(`/posts/${postId}/prepare-share-message`)

    return id
}

export const savePayload = async (data: unknown): Promise<string> => {
    const {
        data: { payload_id }
    } = await httpClient.post(`/posts/save-payload`, { payload: data })

    return payload_id
}

export const checkDraftPost = async (sessionId: string): Promise<{ version: number; post: Message }> => {
    const { data } = await httpClient.get<{ version: number; post: MessageDTO }>(`posts/check-draft/${sessionId}`)

    return data && { version: data.version, post: mapMessageFromDTO(data.post) }
}
